import { useState } from "react";
import { useParams, Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Close,
  CreateNewFolderOutlined,
  FileUploadOutlined,
  KeyboardArrowRight,
} from "@mui/icons-material";
import { LoadingButton, TabPanel } from "@mui/lab";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from "@mui/material";
import DatasetPageLayout from "datasets/components/DatasetPageLayout";
import EmptyStateText from "datasets/components/EmptyStateText";
import useDataset from "datasets/hooks/useDataset";
import useDatasetPrefixes from "datasets/hooks/useDatasetPrefixes";
import FileDirectory from "datasets/pages/files/components/FileDirectory";
import useDatasetFileBreadcrumbs from "datasets/pages/files/hooks/useDatasetFileBreadcrumbs";
import CreateFolderDialog from "files/components/CreateFolderDialog";
import { BreadcrumbButton, BreadcrumbText, MenuCrumb } from "files/components/FileBreadcrumbs";
import FileTree from "files/components/FileTree";
import UploadFileDialog from "files/components/UploadFileDialog";
import useFileAccess from "files/hooks/useFileAccess";
import { resolveObjectKey, resolveRouterPath } from "files/hooks/useFiles";
import FileInfo from "files/types/interfaces/fileInfo";
import { PlusIcon } from "~/assets/icons";
import useUser from "~/contexts/user";
import rumClient, { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API } = import.meta.env;

const EmptyDirectory = ({ canCreate, onClick }: { canCreate: boolean; onClick: () => void }) => {
  return (
    <Stack alignItems="center" gap={3} mt={12}>
      {canCreate ? (
        <>
          <EmptyStateText>
            This dataset has no files.
            <br /> Add a folder below to get started.
          </EmptyStateText>
          <Button startIcon={<PlusIcon />} variant="contained" onClick={onClick}>
            Add Folder
          </Button>
        </>
      ) : (
        <EmptyStateText>This dataset has no files.</EmptyStateText>
      )}
    </Stack>
  );
};

const DatasetFiles = () => {
  const navigate = useNavigate();
  const user = useUser();
  const { datasetId, prefixId, "*": filePath = "" } = useParams();
  const breadcrumbs = useDatasetFileBreadcrumbs();
  const { data: dataset } = useDataset();
  const { data, isLoading, mutate } = useDatasetPrefixes();
  const canCreate = !!dataset?.actions_permitted.includes("edit:catalog-metadata");
  const uploadFilePrefix = resolveObjectKey(filePath, user?.email, "", datasetId);
  const canWrite = useFileAccess(uploadFilePrefix, "write");
  const [error, setError] = useState("");
  const [addFolderDialogOpen, setAddFolderDialogOpen] = useState(false);
  const [newFolderDialogOpen, setNewFolderDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<null | FileInfo>(null);
  const [submitting, setSubmitting] = useState(false);
  const selectedFileKey = resolveObjectKey(resolveRouterPath(selectedFile?.key ?? ""), user?.email);
  const selectedCanWrite = useFileAccess(selectedFileKey, "write");
  const showEmpty = !isLoading && data?.length === 0;
  const showAddFolder = !prefixId && canCreate;
  const showUploadFiles = prefixId && canWrite;

  const onSubmit = async () => {
    if (!selectedFile) {
      return;
    }

    setError("");
    setSubmitting(true);

    try {
      const resp = await fetch(`${VITE_CATALOG_API}/datasets/${datasetId}/file-configs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prefix: selectedFile.key,
        }),
      });

      if (!resp.ok) {
        throw Error("There was a problem trying to add the folder to the dataset");
      }

      trackEvent("dataset_file_config_added", {
        dataset_id: datasetId,
        folder_prefix: selectedFile.key,
      });

      await mutate();
      handleClose();
      navigate(`/data-browser/datasets/${datasetId}/files`);
    } catch (e: any) {
      console.error(e);
      rumClient?.recordError(e);
      setError(e.message);
    }

    setSubmitting(false);
  };

  const handleClose = () => {
    setSelectedFile(null);
    setAddFolderDialogOpen(false);
  };

  return (
    <TabPanel value="files" sx={{ p: 0 }}>
      <DatasetPageLayout>
        {showEmpty ? (
          <EmptyDirectory canCreate={canCreate} onClick={() => setAddFolderDialogOpen(true)} />
        ) : (
          <>
            <Stack gap={2}>
              <Box
                display="flex"
                minHeight={36}
                gap={1}
                justifyContent="space-between"
                alignItems="center"
              >
                <Breadcrumbs separator={<KeyboardArrowRight sx={{ fontSize: 22 }} />}>
                  {breadcrumbs?.map(({ title, prefix, href }, index) => {
                    const isLast = index === breadcrumbs.length - 1;
                    if (breadcrumbs.length === 1) {
                      return (
                        <Box key={href} display="flex" alignItems="center" minHeight={31.5}>
                          <BreadcrumbText key={href}>{title}</BreadcrumbText>
                        </Box>
                      );
                    }

                    if (isLast) {
                      return <MenuCrumb key={href} prefix={prefix} title={title} />;
                    }

                    return (
                      <BreadcrumbButton key={href} component={RouterLink} to={href}>
                        <BreadcrumbText>{title}</BreadcrumbText>
                      </BreadcrumbButton>
                    );
                  })}
                </Breadcrumbs>
                {showUploadFiles && (
                  <Stack direction="row" gap={1}>
                    <Button
                      startIcon={<CreateNewFolderOutlined />}
                      variant="outlined"
                      onClick={() => setNewFolderDialogOpen(true)}
                    >
                      New Folder
                    </Button>
                    <Button
                      startIcon={<FileUploadOutlined />}
                      variant="outlined"
                      onClick={() => setUploadDialogOpen(true)}
                    >
                      Upload Files
                    </Button>
                  </Stack>
                )}
                {showAddFolder && (
                  <Button
                    startIcon={<PlusIcon />}
                    variant="contained"
                    onClick={() => setAddFolderDialogOpen(true)}
                  >
                    Add Folder
                  </Button>
                )}
              </Box>
            </Stack>
            <FileDirectory fileConfigs={data ?? []} configsLoading={isLoading} />
          </>
        )}
      </DatasetPageLayout>
      <Dialog fullWidth open={addFolderDialogOpen}>
        <DialogTitle sx={{ p: 3 }}>Add Folder</DialogTitle>
        <IconButton sx={{ position: "absolute", right: 16, top: 20 }} onClick={handleClose}>
          <Close />
        </IconButton>
        <DialogContent sx={{ gap: 2, py: 0 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <FileTree
            orgFilesOnly
            checkWriteAccess
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
          />
        </DialogContent>
        <DialogActions>
          <Button
            color="secondary"
            disabled={submitting}
            onClick={handleClose}
            sx={{ bgcolor: "grey.100", minWidth: 70, px: 1.25, py: 1 }}
            variant="contained"
          >
            Cancel
          </Button>
          <LoadingButton
            disabled={submitting || !selectedFile || !selectedCanWrite}
            loading={submitting}
            onClick={() => onSubmit()}
            sx={{ minWidth: 70, px: 1.25, py: 1 }}
            type="submit"
            variant="contained"
          >
            Add
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <UploadFileDialog
        uploadPrefix={uploadFilePrefix.endsWith("/") ? uploadFilePrefix : `${uploadFilePrefix}/`}
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
      />
      <CreateFolderDialog
        open={newFolderDialogOpen}
        onClose={() => setNewFolderDialogOpen(false)}
      />
    </TabPanel>
  );
};

export default DatasetFiles;
